// internal-deps
import React, { memo, useEffect, useMemo } from "react";

//components
import { GridContainer } from "@swiggy-private/dls-web-core/dist/components/GridContainer";
import StealDealItemList from "./components/StealDealItemList";
import StealDealLottie from "./components/StealDealLottie";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// types
import { StealDealWidgetProps } from "./types";

//helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { replaceText } from "@swiggy-private/core-ui";

// styles
import { HeaderContainer, CardContainerWrapper } from "./StealDealWidget.styles";
import styles from "./StealDealWidget.scss";

// constants
import { STEAL_DEAL_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";

function StealDealWidget({
    widgetData,
    items,
    onAddStealDeal,
    onImpression,
    onAddStealDealEvent,
    isSnaccStorefrontV2,
}: StealDealWidgetProps): React.ReactElement | null {
    const title = replaceText(widgetData?.title || "", styles.imageStyle, true);
    const isUnlockedState = widgetData?.movMet || false;

    const renderItems = useMemo(
        () => (
            <StealDealItemList
                stealDealItems={items}
                isUnlockedState={isUnlockedState}
                onAddStealDeal={onAddStealDeal}
                onAddStealDealEvent={onAddStealDealEvent}
                isSnaccStorefrontV2={isSnaccStorefrontV2}
            />
        ),
        [items, isUnlockedState, onAddStealDeal, onAddStealDealEvent, isSnaccStorefrontV2],
    );

    const gridItems = useMemo(() => ({ rows: [{ columns: [renderItems] }] }), [renderItems]);

    // Trigger impression whenever lock state changes (both true and false)
    useEffect(() => {
        if (!isEmpty(items)) onImpression?.();
    }, [isUnlockedState, onImpression, items]);

    if (isEmpty(items)) {
        return null;
    }

    return (
        <CardContainerWrapper
            cornerRadius="medium"
            isUnlocked={isUnlockedState}
            data-testid={STEAL_DEAL_WIDGET_TEST_IDS.CONTAINER}
        >
            {/* Lottie Animation Overlay */}
            <StealDealLottie isVisible={isUnlockedState} />

            <HeaderContainer
                data-testid={
                    isUnlockedState
                        ? STEAL_DEAL_WIDGET_TEST_IDS.UNLOCKED_STATE
                        : STEAL_DEAL_WIDGET_TEST_IDS.LOCKED_STATE
                }
            >
                {title && (
                    <Text
                        variant="V2.overline.neutral.black"
                        color={isUnlockedState ? "rating_Very_Good" : "special_button_text"}
                        style={{ textTransform: "uppercase" }}
                        data-testid={STEAL_DEAL_WIDGET_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                )}

                {widgetData?.subtitle && (
                    <Text
                        variant="V2.body2.neutral.bold"
                        color="text_High_Emphasis"
                        data-testid={STEAL_DEAL_WIDGET_TEST_IDS.SUBTITLE}
                    >
                        {widgetData?.subtitle}
                    </Text>
                )}
            </HeaderContainer>

            <GridContainer
                spaceBetweenTitleAndContent="empty"
                interItemSpacing="small"
                items={gridItems}
                clearTitleDefaultMargin
                hasCustomSpacing
                title=""
                background="transparent"
            />
        </CardContainerWrapper>
    );
}

export default memo(StealDealWidget);
